import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_golfbazar/artifacts/d3dy0w6h_Skjermbilde%202025-12-20%20125648.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable Navbar component for GolfMarked
 * 
 * @param {Object} props
 * @param {boolean} props.showBackButton - Show back button instead of auth controls
 * @param {string} props.backTo - URL to navigate to when back button is clicked (default: "/")
 * @param {boolean} props.showWedgeGuide - Show Wedge Guide link (default: true)
 * @param {boolean} props.minimal - Minimal version with just logo (for auth pages)
 */
const Navbar = ({ 
  showBackButton = false, 
  backTo = "/",
  showWedgeGuide = true,
  minimal = false 
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, getAuthHeaders } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, {
          headers: getAuthHeaders()
        });
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error("Error fetching notification count:", error);
      }
    };

    if (isAuthenticated()) {
      fetchUnreadCount();
    }
  }, [isAuthenticated, getAuthHeaders]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeaders()
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleNotificationClick = async () => {
    if (!showNotifications) {
      await fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationItemClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    setShowNotifications(false);
    if (notification.listing_id) {
      navigate(`/listing/${notification.listing_id}`);
    }
  };

  if (minimal) {
    return (
      <header className="bg-white border-b border-gray-100">
        <div className="px-4 py-3 max-w-7xl mx-auto">
          <div className="flex justify-center">
            <img 
              src={LOGO_URL}
              alt="GolfMarked" 
              className="h-10 w-auto cursor-pointer"
              onClick={() => navigate("/")}
            />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-white sticky top-0 z-40 border-b border-gray-100">
      <div className="px-4 py-3 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <img 
            src={LOGO_URL}
            alt="GolfMarked" 
            className="h-8 lg:h-10 w-auto cursor-pointer"
            onClick={() => navigate("/")}
            data-testid="logo-home-link"
          />
          <div className="flex items-center gap-4">
            {showWedgeGuide && (
              <button 
                onClick={() => navigate("/wedge-grind-guide")}
                className="text-sm text-gray-500 hover:text-gray-700 hidden sm:block"
              >
                Wedge Guide
              </button>
            )}
            {showBackButton ? (
              <Button
                variant="outline"
                onClick={() => navigate(backTo)}
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Tilbake
              </Button>
            ) : isAuthenticated() ? (
              <div className="flex items-center gap-3">
                {/* Notifications */}
                <div className="relative">
                  <button
                    onClick={handleNotificationClick}
                    className="relative p-2 text-gray-500 hover:text-gray-700"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  
                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-3 border-b border-gray-100 flex justify-between items-center">
                        <span className="font-medium text-gray-900">Varslinger</span>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-green-600 hover:text-green-700"
                          >
                            Marker alle som lest
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Ingen varslinger
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => handleNotificationItemClick(notification)}
                              className={`p-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${
                                !notification.read ? 'bg-green-50' : ''
                              }`}
                            >
                              <p className="text-sm text-gray-900 font-medium">
                                {notification.title}
                              </p>
                              <p className="text-sm text-gray-600 mt-0.5">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notification.created_at).toLocaleDateString('nb-NO', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <span className="text-sm text-gray-600">{user?.name?.split(' ')[0]}</span>
                <button 
                  onClick={() => {
                    logout();
                    toast.success("Logget ut");
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Logg ut
                </button>
              </div>
            ) : (
              <button 
                onClick={() => navigate("/login")}
                className="text-sm font-medium text-gray-700 hover:text-green-700"
              >
                Logg inn
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
